import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { articles, categories } from "@/mocks/data";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <main className="min-h-screen pt-16 flex items-center justify-center bg-background-50">
        <div className="text-center">
          <span className="w-20 h-20 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mx-auto mb-4">
            <i className="ri-error-warning-line text-3xl"></i>
          </span>
          <h1 className="font-heading text-xl font-bold text-foreground-900 mb-2">
            文章未找到
          </h1>
          <p className="text-foreground-500 text-sm mb-4">
            你访问的页面不存在或已被移除
          </p>
          <Link
            to="/"
            className="inline-flex bg-primary-500 hover:bg-primary-600 text-white px-6 py-2.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  return (
    <main className="min-h-screen pt-16 bg-background-50">
      {/* Article Hero Image */}
      <section className="relative w-full h-[360px] md:h-[460px] overflow-hidden">
        <img
          src={article.imageUrl}
          alt={article.title}
          title={article.title}
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/15 to-black/55"></div>
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10 z-10 max-w-[1280px] mx-auto">
          <div className="max-w-3xl">
            {category && (
              <Link
                to={`/category/${category.slug}`}
                className="inline-flex bg-accent-500 text-white text-xs px-3 py-1 rounded-full mb-3 cursor-pointer"
              >
                {category.name}
              </Link>
            )}
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-white mb-3 leading-tight">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-white/70 text-sm flex-wrap">
              <span>{article.author}</span>
              <span className="w-1 h-1 rounded-full bg-white/40"></span>
              <span>{article.publishDate}</span>
              <span className="w-1 h-1 rounded-full bg-white/40"></span>
              <span>{article.readTime}阅读</span>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="bg-background-50">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: "目的地" },
              { label: article.category, to: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <div className="max-w-4xl mx-auto mt-6">
            <div className="flex items-center gap-2 mb-8 flex-wrap">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="bg-accent-100 text-accent-800 text-xs px-3 py-1 rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>

            <article
              className="prose-custom bg-white rounded-xl p-6 md:p-10 border border-background-200/70 mb-10"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="bg-white rounded-xl p-6 border border-background-200/70 flex items-center gap-4 mb-12">
              <div className="w-14 h-14 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                <span className="font-heading text-primary-600 font-bold text-lg">
                  {article.author.charAt(0)}
                </span>
              </div>
              <div>
                <p className="font-medium text-foreground-900 text-sm">{article.author}</p>
                <p className="text-xs text-foreground-500">亚洲天堂签约旅行达人</p>
              </div>
            </div>

            {relatedArticles.length > 0 && (
              <div className="mb-12">
                <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">
                  相关推荐
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.slug}
                      to={`/article/${ra.slug}`}
                      className="group bg-white rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200/50 transition-all cursor-pointer"
                    >
                      <div className="relative h-40 overflow-hidden">
                        <img
                          src={ra.cardImageUrl}
                          alt={ra.title}
                          title={ra.title}
                          className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="font-heading text-sm font-bold text-foreground-900 mb-1 line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {ra.title}
                        </h3>
                        <p className="text-xs text-foreground-400">
                          {ra.readTime}阅读 · {ra.publishDate}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}